import { Box, Button, Card, Chip, Grid, Typography } from '@mui/material';
import { ReactElement } from 'react';
import { FormState } from 'react-hook-form';
import { useSnackbar } from '@localstack/services';

import { ExperimentCardType, ExperimentProps } from '../types';
import { isExperimentRunning } from '../utils';

import { WrapInToolTip } from './WrapInTooltip';
import { useExperimentCardStyles } from './common-styles';

type ExperimentCardProps = {
  experimentCard: ExperimentCardType;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  formState: FormState<any>;
  disabled: boolean;
} & ExperimentProps;

export const ExperimentCard = ({
  experimentCard,
  onStopExperiment,
  onUpsertExperiment,
  experiment: experimentState,
  alert,
  formState,
  disabled,
}: ExperimentCardProps): ReactElement => {
  const { showSnackbar } = useSnackbar();
  const classes = useExperimentCardStyles();
  const isRunning = isExperimentRunning(experimentState);
  const { title, description, options, template } = experimentCard;
  const { isValid: formIsValid } = formState;

  const copyButtonClickHandler = async () => {
    await navigator.clipboard.writeText(JSON.stringify(template, null, 4));
    showSnackbar({ message: 'Copied to clipboard', severity: 'success' });
  };

  return (
    <Card className={classes.card} variant="outlined">
      <Box mb={1}>
        <Grid container justifyContent="space-between" alignItems="center">
          <Grid item minHeight={32}>
            {/* min height is set to avoid the box size layout shift when status is shown */}
            <Box display="flex" alignItems="center">
              <Typography variant="h5" className={classes.heading}>
                {title}
              </Typography>
            </Box>
          </Grid>
          {isRunning && (
            <Grid item>
              <Chip className={classes.running} label="Running" />
            </Grid>
          )}
        </Grid>
      </Box>
      <Box mb={2}>
        <Typography>{description}</Typography>
      </Box>
      <Box mb={2}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6} style={{ paddingTop: '15px' }}>
            {options}
          </Grid>

          <Grid item xs={12} md={6}>
            <Grid container justifyContent="flex-end" height="100%">
              <Grid item display="flex" alignItems="flex-end" justifyContent="flex-end" height="100%" gap={1}>
                <Button variant="outlined" onClick={copyButtonClickHandler}>
                  Copy Chaos API Template
                </Button>
                <Box>
                  {!isRunning && (
                    <WrapInToolTip title={alert}>
                      <span>
                        <Button
                          title="Run Experiment"
                          variant="contained"
                          color="primary"
                          onClick={() => onUpsertExperiment(template, experimentState)}
                          disabled={!!alert || !formIsValid || disabled}
                        >
                          Run Experiment
                        </Button>
                      </span>
                    </WrapInToolTip>
                  )}
                  {isRunning && (
                    <WrapInToolTip title={alert}>
                      <span>
                        <Button
                          title="Stop Experiment"
                          variant="outlined"
                          className={classes.secondaryBtn}
                          onClick={() => onStopExperiment(experimentState)}
                          disabled={!!alert || disabled}
                        >
                          Stop Experiment
                        </Button>
                      </span>
                    </WrapInToolTip>
                  )}
                </Box>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Box>
    </Card>
  );
};
